<template>
  <svg :id=svgId></svg>
</template>

<script>
import * as d3 from 'd3';

export default {
  props: {
    dataInput: Array,
    width: Number,
    height: Number,
    suggest: Boolean,
  },
  data() {
    return {
      svgId: `radarchart-${this._uid}`, // Generate a unique ID for the SVG element
    };
  },
  mounted() {
    this.drawChart();
  },
  methods: {
    drawChart() {
      const data = this.dataInput;
      const suggestions = { lower: 30, middle: 50, upper: 80 };

      // Clear previous SVG content
      const container = d3.select(`#${this.svgId}`);
      container.selectAll('*').remove();

      // Margins and dimensions
      const margin = { top: 60, right: 80, bottom: 60, left: 80 };
      const width = this.width;
      const height = this.height;
      const radius = Math.min(width, height) / 2;

      // Create SVG and group, centered
      const svg = container
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .style('font-family', 'Times New Roman')
        .append('g')
        .attr('transform', `translate(${(width + margin.left + margin.right) / 2}, ${(height + margin.top + margin.bottom) / 2})`);

      // Grey background box (drawn first)
      svg
        .append('rect')
        .attr('x', -width)
        .attr('y', -height)
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .attr('rx', 12)
        .attr('fill', 'none')
        .attr('stroke', '#ccc')
        .attr('stroke-width', 1);

      // Scales
      const scale = d3.scaleLinear()
        .domain([0, 100])
        .range([0, radius]);

      const angle = d3.scaleBand()
        .domain(data.map(d => d.variable))
        .range([0, 2 * Math.PI]);

      // Draw circular grid lines and labels at 20%, 40%, 60%, 80%, 100%
      const gridLevels = 5;
      const gridValues = d3.range(1, gridLevels + 1).map(i => i * 100 / gridLevels);

      const grid = svg.append('g').attr('class', 'grid');

      grid.selectAll('circle')
        .data(gridValues)
        .enter()
        .append('circle')
        .attr('r', d => scale(d))
        .attr('fill', 'none')
        .attr('stroke', '#ccc')
        .attr('stroke-dasharray', '3,3');

      // Grid value labels
      grid.selectAll('text')
        .data(gridValues)
        .enter()
        .append('text')
        .attr('x', 5)
        .attr('y', d => -scale(d))
        .text(d => `${d}%`)
        .style('font-size', '10px')
        .style('fill', '#666');

      // Draw axis lines
      svg.selectAll('.axis-line')
        .data(data)
        .enter()
        .append('line')
        .attr('class', 'axis-line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', d => scale(100) * Math.sin(angle(d.variable)))
        .attr('y2', d => -scale(100) * Math.cos(angle(d.variable)))
        .attr('stroke', '#aaa')
        .attr('stroke-dasharray', '3,3');

      // Radar line generator
      const line = d3.lineRadial()
        .angle(d => angle(d.variable))
        .radius(d => scale(d.value))
        .curve(d3.curveLinearClosed);

      // Draw filled radar area
      svg.append('path')
        .datum(data)
        .attr('d', line)
        .attr('fill', '#6092C0')
        .attr('opacity', 0.3)
        .attr('stroke', '#305080')
        .attr('stroke-width', 2);

      // Draw data points as circles
      svg.selectAll('.data-point')
        .data(data)
        .enter()
        .append('circle')
        .attr('class', 'data-point')
        .attr('cx', d => scale(d.value) * Math.sin(angle(d.variable)))
        .attr('cy', d => -scale(d.value) * Math.cos(angle(d.variable)))
        .attr('r', 4)
        .attr('fill', '#305080')
        .attr('stroke', 'white')
        .attr('stroke-width', 1.5);

      // Add labels with improved positioning and flipping
      svg.selectAll('.label')
        .data(data)
        .enter()
        .append('text')
        .attr('class', 'label')
        .attr('font-size', '12px')
        .attr('font-weight', 'bold')
        .attr('fill', '#333')
        .attr('text-anchor', d => {
          const a = angle(d.variable);
          return (a > Math.PI / 2 && a < 3 * Math.PI / 2) ? 'end' : 'start';
        })
        .attr('transform', d => {
          const labelRadius = scale(100) + 20;
          const x = labelRadius * Math.sin(angle(d.variable));
          const y = -labelRadius * Math.cos(angle(d.variable));
          let rotation = (angle(d.variable) * 180 / Math.PI) - 90;

          // Flip label if on left side
          if (angle(d.variable) > Math.PI / 2 && angle(d.variable) < 3 * Math.PI / 2) {
            rotation += 180;
          }

          return `translate(${x},${y}) rotate(${rotation})`;
        })
        .text(d => d.variable);

      // Draw suggestion circles only if enabled
      if (this.suggest) {
        const suggestionRadii = [suggestions.lower, suggestions.middle, suggestions.upper].map(v => scale(v));
        const suggestionColors = ['#F87B7B', '#FD9B41', '#14AE5C'];

        svg.selectAll('.suggestion-circle')
          .data(suggestionRadii)
          .enter()
          .append('circle')
          .attr('class', 'suggestion-circle')
          .attr('r', d => d)
          .attr('fill', 'none')
          .attr('stroke', (d, i) => suggestionColors[i])
          .attr('stroke-width', 1.5)
          .attr('stroke-dasharray', '5,5');
      }
    }
  },
};
</script>